import { useEffect, useRef, useState } from 'react';
import { Landmark, User, Send, Loader2, MessageSquare } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { UserAvatar } from '../../components/ui/UserAvatar';

const API = import.meta.env.VITE_API_URL || '/api/v1';

interface Conversa {
  id: string;
  alunoId: string;
  responsavelId: string;
  professorId: string | null;
  ultimoTexto: string | null;
  criadoEm: string;
  atualizadoEm: string;
  aluno: { id: string; nome: string };
  responsavel: { id: string; nome: string; usuarioId: string; fotoUrl?: string | null };
  escola: { id: string; nome: string };
  unreadCount: number;
}

export interface ChatDesktopNavState {
  conversaId: string;
  draftText?: string;
  seedConversa?: Conversa;
}

interface Mensagem {
  id: string;
  conversaId: string;
  remetenteId: string;
  texto: string;
  lida: boolean;
  criadoEm: string;
  remetente: { id: string; nome: string; perfil: string; fotoUrl?: string | null };
}

export function ChatDesktop() {
  const token = localStorage.getItem('ac_token');
  const user = useAuthStore((s) => s.user);

  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [selectedConversaId, setSelectedConversaId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Mensagem[]>([]);
  const [text, setText] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const isProfessor = user?.perfil === 'PROFESSOR';
  const targetEndpoint = isProfessor
    ? `${API}/operacao/conversas/professor/${user.id}`
    : `${API}/operacao/conversas/escola/${user?.escolaId}`;

  useEffect(() => {
    if (!user) return;
    loadConversas();
    const interval = setInterval(loadConversas, 5000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!selectedConversaId) return;
    loadMessages();
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [selectedConversaId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadConversas() {
    try {
      const res = await fetch(targetEndpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setConversas(await res.json());
      }
    } catch { /* fail silently */ }
    setLoadingList(false);
  }

  async function loadMessages() {
    if (!selectedConversaId) return;
    try {
      const res = await fetch(`${API}/operacao/conversas/${selectedConversaId}/mensagens`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMessages(await res.json());
      }
    } catch { /* fail silently */ }
    setLoadingMessages(false);
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim() || !selectedConversaId || sending) return;

    setSending(true);
    const textToSend = text;
    setText('');

    try {
      const res = await fetch(`${API}/operacao/conversas/${selectedConversaId}/mensagens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ texto: textToSend }),
      });

      if (res.ok) {
        const newMsg = await res.json();
        setMessages((prev) => [...prev, newMsg]);
        loadConversas(); // Reload list to update last message
      } else {
        setText(textToSend);
      }
    } catch {
      setText(textToSend);
    }
    setSending(false);
  }

  const selectedConversa = conversas.find((c) => c.id === selectedConversaId);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', margin: -20, background: '#f8fafc' }}>
      {/* Sidebar List */}
      <div
        style={{
          width: 320,
          borderRight: '1px solid #e2e8f0',
          background: 'white',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>Mensagens com Pais</h3>
          <p style={{ color: '#94a3b8', fontSize: '0.72rem', margin: '2px 0 0 0' }}>
            {isProfessor ? 'Minhas turmas vinculadas' : 'Secretaria Geral'}
          </p>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 0' }}>
          {loadingList && conversas.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
              <Loader2 size={22} style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} />
            </div>
          ) : conversas.length > 0 ? (
            conversas.map((c) => {
              const isSelected = c.id === selectedConversaId;
              const time = new Date(c.atualizadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

              return (
                <div
                  key={c.id}
                  onClick={() => {
                    setSelectedConversaId(c.id);
                    setLoadingMessages(true);
                  }}
                  style={{
                    padding: '14px 18px',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    cursor: 'pointer',
                    background: isSelected ? '#f1f5f9' : 'transparent',
                    borderLeft: isSelected ? '4px solid #7c3aed' : '4px solid transparent',
                    transition: 'background 0.2s',
                  }}
                >
                  <UserAvatar
                    src={c.responsavel.fotoUrl}
                    name={c.responsavel.nome}
                    role="RESPONSAVEL"
                    size="md"
                    style={{ flexShrink: 0 }}
                  />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <h4 style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.responsavel.nome}
                      </h4>
                      <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{time}</span>
                    </div>
                    <p style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, margin: '2px 0' }}>
                      Aluno: {c.aluno.nome}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.ultimoTexto || 'Nenhuma mensagem.'}
                    </p>
                  </div>

                  {c.unreadCount > 0 && (
                    <div style={{ background: '#7c3aed', color: 'white', fontSize: '0.68rem', fontWeight: 700, borderRadius: '10px', padding: '2px 6px' }}>
                      {c.unreadCount}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
              <MessageSquare size={24} style={{ margin: '0 auto 10px auto', display: 'block', color: '#cbd5e1' }} />
              <p style={{ fontSize: '0.78rem', margin: 0 }}>Nenhuma conversa ativa no momento.</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Box */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f1f5f9' }}>
        {selectedConversa ? (
          <>
            {/* Chat Box Header */}
            <div
              style={{
                background: 'white',
                padding: '14px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <UserAvatar
                src={selectedConversa.responsavel.fotoUrl}
                name={selectedConversa.responsavel.nome}
                role="RESPONSAVEL"
                size="md"
                style={{ flexShrink: 0 }}
              />
              <div>
                <h4 style={{ fontSize: '0.9rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>
                  {selectedConversa.responsavel.nome}
                </h4>
                <p style={{ fontSize: '0.72rem', color: '#64748b', margin: '2px 0 0 0' }}>
                  Contexto Filho: <strong>{selectedConversa.aluno.nome}</strong> · Escola: {selectedConversa.escola.nome}
                </p>
              </div>
            </div>

            {/* Messages Area */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {loadingMessages && messages.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                  <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} />
                </div>
              ) : (
                messages.map((m) => {
                  const isMine = m.remetenteId === user?.id;
                  const senderPhoto = isMine ? user?.fotoUrl : (m.remetente.fotoUrl || selectedConversa.responsavel.fotoUrl);
                  const senderName = isMine ? (user?.nome || 'Você') : m.remetente.nome;
                  const senderRole = isMine ? (user?.cargo || user?.perfil) : m.remetente.perfil;

                  return (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isMine ? 'flex-end' : 'flex-start',
                        width: '100%',
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 7,
                        maxWidth: '75%',
                        flexDirection: isMine ? 'row-reverse' : 'row',
                      }}>
                        <UserAvatar
                          src={senderPhoto}
                          name={senderName}
                          role={senderRole}
                          size={26}
                          style={{ flexShrink: 0, marginBottom: 2 }}
                        />
                        <div
                          style={{
                            padding: '10px 14px',
                            borderRadius: isMine ? '16px 16px 2px 16px' : '16px 16px 16px 2px',
                            background: isMine ? '#7c3aed' : 'white',
                            color: isMine ? 'white' : '#1e293b',
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            border: isMine ? 'none' : '1px solid #e2e8f0',
                            wordBreak: 'break-word',
                          }}
                        >
                          {m.texto}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.62rem', color: '#94a3b8', marginTop: 3, marginRight: isMine ? 33 : 0, marginLeft: !isMine ? 33 : 0 }}>
                        {new Date(m.criadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Form */}
            <form
              onSubmit={handleSend}
              style={{
                background: 'white',
                padding: '16px 24px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                gap: 12,
                alignItems: 'center',
              }}
            >
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Escreva sua resposta..."
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#1e293b',
                  fontSize: '0.88rem',
                  outline: 'none',
                }}
              />
              <button
                type="submit"
                disabled={!text.trim() || sending}
                style={{
                  background: text.trim() ? '#7c3aed' : '#cbd5e1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  width: 44,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'background 0.2s',
                }}
              >
                {sending ? (
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </form>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
            <MessageSquare size={48} style={{ marginBottom: 16, color: '#cbd5e1' }} />
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0, color: '#64748b' }}>Selecione um chat</h3>
            <p style={{ fontSize: '0.82rem', margin: '4px 0 0 0' }}>Escolha um responsável na barra lateral para ver as mensagens.</p>
          </div>
        )}
      </div>
    </div>
  );
}
